import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  LineChart,
  Lock,
  Wallet,
  TrendingUp,
  Heart,
  ArrowRight,
  PiggyBank,
  Target,
  Calculator,
  LayoutDashboard,
  Users,
  FileText,
  BadgeIndianRupee,
  Building2,
  Coins,
  Landmark,
  Car,
  Gem,
  Bitcoin,
  CreditCard,
  Home,
  Briefcase,
  TreePine,
  BarChart3,
  Bell,
  CheckCircle2,
  Star,
  Globe,
  BookOpen,
  Clock,
  ArrowUpRight,
  Layers,
  Inbox,
  Zap,
  CalendarClock,
  AlertTriangle,
  AreaChart,
  SlidersHorizontal,
  BrainCircuit,
} from "lucide-react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { CALCULATOR_LIST } from "../components/calculators/calculators";
import { api, fmtINR } from "../lib/api";
import {
  ResponsiveContainer,
  AreaChart as ReAreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";

// ─── Data ──────────────────────────────────────────────────────────────────────

const HERO_GROWTH = Array.from({ length: 11 }).map((_, i) => ({
  year: 2025 + i,
  value: Math.round(1500000 * 1.13 ** i),
  invested: 1500000 + i * 600000,
}));

const ALLOC = [
  { name: "Equity", value: 45 },
  { name: "Debt", value: 22 },
  { name: "Real Estate", value: 18 },
  { name: "Gold", value: 8 },
  { name: "Cash", value: 7 },
];
const COLORS = ["#10b981", "#1e3a8a", "#f59e0b", "#a855f7", "#64748b"];

const ASSET_TYPES = [
  { icon: BarChart3, label: "Mutual Funds" },
  { icon: TrendingUp, label: "Stocks" },
  { icon: Landmark, label: "Fixed Deposits" },
  { icon: Coins, label: "Gold & Silver" },
  { icon: Building2, label: "Real Estate" },
  { icon: Bitcoin, label: "Crypto" },
  { icon: Briefcase, label: "EPF / PPF" },
  { icon: TreePine, label: "NPS" },
  { icon: Car, label: "Vehicles" },
  { icon: Globe, label: "Foreign Assets" },
  { icon: Gem, label: "Collectibles" },
  { icon: BookOpen, label: "Bonds" },
  { icon: Home, label: "REITs" },
  { icon: CreditCard, label: "Cash & Savings" },
  { icon: Layers, label: "Unlisted Equity" },
  { icon: PiggyBank, label: "Chit Funds" },
  { icon: Inbox, label: "Other Assets" },
];

const LIABILITIES = [
  { label: "Home Loan", emi: "₹42,000/mo", due: "5 days", status: "upcoming" },
  { label: "Car Loan", emi: "₹12,500/mo", due: "12 days", status: "ok" },
  { label: "Personal Loan", emi: "₹8,200/mo", due: "18 days", status: "ok" },
  { label: "Credit Card", emi: "₹6,400 due", due: "2 days", status: "urgent" },
];

const VAULT_FEATURES = [
  {
    icon: Lock,
    title: "AES-256 encrypted",
    desc: "Every document encrypted end-to-end. Even we can't read your files.",
  },
  {
    icon: Users,
    title: "Tiered nominee access",
    desc: "Trustees, executors and nominees each get exactly the right level of access.",
  },
  {
    icon: FileText,
    title: "Document vault",
    desc: "Will, insurance, property deeds, crypto seeds — all in one secure place.",
  },
  {
    icon: Bell,
    title: "Dead-man switch",
    desc: "Inactivity-based release so your family is never locked out when it matters.",
  },
  {
    icon: ShieldCheck,
    title: "DPDP ready",
    desc: "Built to comply with India's Digital Personal Data Protection Act.",
  },
  {
    icon: Clock,
    title: "Time-locked access",
    desc: "Set conditions for when and how your data becomes visible to nominees.",
  },
];

const FAQ_ITEMS = [
  {
    q: "How is AccessWealth different from a portfolio tracker?",
    a: "Portfolio trackers show your investment returns. AccessWealth shows your entire financial life — assets across 17 categories, liabilities with EMI tracking, goals with timelines, net worth projections, and family continuity. It's the difference between a speedometer and a full cockpit.",
  },
  {
    q: "Can I track liabilities like loans and EMIs?",
    a: "Yes — and this is one of our strongest features. Add home loans, car loans, personal loans, credit cards. AccessWealth tracks outstanding amounts, monthly EMIs, due dates, and calculates your true net worth (assets minus liabilities) automatically.",
  },
  {
    q: "What asset types can I track?",
    a: "17 types: mutual funds, stocks, fixed deposits, gold, real estate, crypto, EPF/PPF, NPS, vehicles, foreign assets, collectibles, bonds, REITs, cash, unlisted equity, chit funds, and more. If you own it, you can track it.",
  },
  {
    q: "How do the wealth projections work?",
    a: "You input your current portfolio and expected contribution rate. AccessWealth projects your net worth across three scenarios — conservative (6%), moderate (10%), and aggressive (14%) — over your lifetime, adjusted for inflation. You can also model specific goals like retirement or FIRE.",
  },
  {
    q: "Is my financial data safe?",
    a: "All data is encrypted at rest (AES-256) and in transit (TLS 1.3). Vault documents use zero-trust access — even our engineers can't read your files. We're built to comply with India's DPDP Act.",
  },
  {
    q: "Does it connect to my bank automatically?",
    a: "Account Aggregator (RBI) integration is coming soon. For now, you add holdings manually — which most users say actually improves their financial awareness significantly.",
  },
  {
    q: "Can I cancel my premium plan anytime?",
    a: "Yes, one click from settings. Your data stays with you forever — we never delete records when you downgrade.",
  },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function Landing_Copy() {
  return (
    <div className="antialiased">
      <Navbar />
      <Hero />
      <TrustBar />
      <NetWorthSection />
      <AssetCoverage />
      <LiabilitySection />
      <ProjectionsSection />
      <CalculatorsPreview />
      <VaultSection />
      <Testimonials />
      <FAQ />
      <ContactSection />
      <CTA />
      <Footer />
    </div>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative mesh-bg overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 pl-1 pr-4 py-1 shadow-sm mb-6">
              <span className="grid h-6 w-6 place-items-center rounded-full bg-emerald-500 text-white text-[10px] font-bold">
                NEW
              </span>
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
                Account Aggregator integration — Coming soon
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-slate-900 dark:text-white leading-[1.05]"
            >
              Stop worrying
              <br />
              about money.
              <br />
              <span className="gradient-text">Start building wealth.</span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-xl leading-relaxed">
              AccessWealth is your personal financial command center. Track
              every asset, every liability, every goal — and always know exactly
              where you stand and where you're headed.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                {
                  icon: Wallet,
                  label: "Track all your assets",
                  sub: "17 categories",
                },
                {
                  icon: CreditCard,
                  label: "Manage every liability",
                  sub: "EMIs, loans, cards",
                },
                {
                  icon: TrendingUp,
                  label: "See your future wealth",
                  sub: "25-year projections",
                },
              ].map(({ icon: Icon, label, sub }) => (
                <div
                  key={label}
                  className="flex items-start gap-3 rounded-xl bg-white/60 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 px-4 py-3"
                >
                  <div className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 flex-shrink-0">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                      {label}
                    </div>
                    <div className="text-[11px] text-slate-500">{sub}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/register"
                data-testid="hero-cta-primary"
                className="btn-primary"
              >
                Start free — no card needed <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/calculators"
                data-testid="hero-cta-secondary"
                className="btn-secondary"
              >
                Try the calculators
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6">
            <HeroDashboardPreview />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroDashboardPreview() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-gradient-to-br from-emerald-500/20 via-transparent to-blue-600/15 blur-2xl rounded-3xl -z-10" />
      <div className="card-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs text-slate-500">Total Net Worth</div>
            <div className="num-mono text-3xl font-bold text-slate-900 dark:text-white">
              {fmtINR(9026000)}
            </div>
            <div className="mt-1 text-xs text-emerald-600 font-semibold flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> +18.4% YoY
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-wider bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-2 py-1 rounded-full font-semibold">
            Live preview
          </span>
        </div>
        <div className="h-44">
          <ResponsiveContainer>
            <ReAreaChart data={HERO_GROWTH}>
              <defs>
                <linearGradient id="hero-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="rgba(148,163,184,0.18)"
              />
              <XAxis
                dataKey="year"
                tick={{ fontSize: 10 }}
                stroke="rgba(100,116,139,0.5)"
              />
              <YAxis
                tickFormatter={(v) => fmtINR(v, { compact: true })}
                tick={{ fontSize: 10 }}
                stroke="rgba(100,116,139,0.5)"
              />
              <Tooltip formatter={(v) => fmtINR(v)} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#10b981"
                strokeWidth={2.5}
                fill="url(#hero-grad)"
              />
            </ReAreaChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <MiniCard
            icon={Wallet}
            label="Assets"
            value={fmtINR(13200000)}
            accent="text-slate-900 dark:text-white"
          />
          <MiniCard
            icon={CreditCard}
            label="Liabilities"
            value={fmtINR(4174000)}
            accent="text-red-500"
          />
          <MiniCard
            icon={Target}
            label="Health"
            value="82 / 100"
            accent="text-emerald-600"
          />
        </div>
      </div>

      <div className="hidden md:block absolute -bottom-6 -left-6 card-soft p-4 w-56">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
          Allocation
        </div>
        <div className="flex items-center gap-3">
          <div className="h-20 w-20">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={ALLOC}
                  dataKey="value"
                  innerRadius={22}
                  outerRadius={36}
                  paddingAngle={2}
                >
                  {ALLOC.map((_, i) => (
                    <Cell key={i} fill={COLORS[i]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid gap-1">
            {ALLOC.slice(0, 4).map((a, i) => (
              <div key={a.name} className="flex items-center gap-1.5">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: COLORS[i] }}
                />
                <span className="text-[10px] text-slate-600 dark:text-slate-300">
                  {a.name}
                </span>
                <span className="text-[10px] num-mono ml-auto text-slate-500">
                  {a.value}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-3">
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-wider">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`num-mono text-sm font-bold mt-0.5 ${accent}`}>
        {value}
      </div>
    </div>
  );
}

// ─── Trust Bar ─────────────────────────────────────────────────────────────────

function TrustBar() {
  return (
    <section className="border-y border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-950/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {[
            { value: "17", label: "Asset types supported" },
            { value: "25+", label: "Financial calculators" },
            { value: "256-bit", label: "AES encryption" },
            { value: "₹0", label: "To get started" },
            { value: "1 place", label: "For your entire financial life" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="num-mono text-xl font-bold text-slate-900 dark:text-white">
                {s.value}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Net Worth / Dashboard Section ────────────────────────────────────────────

function NetWorthSection() {
  return (
    <section
      id="features"
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20"
    >
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">
            Your Financial Dashboard
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white">
            Know your real
            <br />
            <span className="gradient-text">net worth. Always.</span>
          </h2>
          <p className="mt-5 text-slate-600 dark:text-slate-300 text-base leading-relaxed">
            Most people have a vague sense of what they own. AccessWealth gives
            you the exact number — assets minus liabilities — updated every time
            you log in. No spreadsheets. No guesswork.
          </p>
          <div className="mt-8 space-y-4">
            {[
              {
                icon: LayoutDashboard,
                title: "One living dashboard",
                desc: "Net worth, allocation breakdown, health score, and goal progress — all visible at a glance.",
              },
              {
                icon: AreaChart,
                title: "Wealth health score",
                desc: "A 0–100 score that tells you how balanced, diversified, and goal-aligned your portfolio is.",
              },
              {
                icon: SlidersHorizontal,
                title: "Full asset allocation view",
                desc: "See exactly how your wealth is spread across equity, debt, real estate, gold, and more.",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 flex-shrink-0 mt-0.5">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900 dark:text-white text-sm">
                    {title}
                  </div>
                  <div className="text-sm text-slate-500 mt-0.5 leading-relaxed">
                    {desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Net worth breakdown card */}
        <div className="space-y-4">
          <div className="card-soft p-6">
            <div className="text-xs text-slate-500 mb-1">True Net Worth</div>
            <div className="flex items-end gap-2 mb-5">
              <div className="num-mono text-4xl font-bold text-slate-900 dark:text-white">
                {fmtINR(9026000)}
              </div>
              <div className="text-xs text-emerald-600 font-semibold mb-1.5 flex items-center gap-0.5">
                <TrendingUp className="h-3 w-3" /> +18.4%
              </div>
            </div>
            {/* Visual breakdown bar */}
            <div className="flex rounded-full overflow-hidden h-2.5 mb-4">
              {ALLOC.map((a, i) => (
                <div
                  key={a.name}
                  style={{ width: `${a.value}%`, background: COLORS[i] }}
                />
              ))}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {ALLOC.map((a, i) => (
                <div key={a.name} className="flex items-center gap-1.5">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: COLORS[i] }}
                  />
                  <span className="text-xs text-slate-600 dark:text-slate-400">
                    {a.name}
                  </span>
                  <span className="text-xs num-mono text-slate-500">
                    {a.value}%
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                  Total Assets
                </div>
                <div className="num-mono font-bold text-slate-900 dark:text-white">
                  {fmtINR(13200000)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                  Total Liabilities
                </div>
                <div className="num-mono font-bold text-red-500">
                  {fmtINR(4174000)}
                </div>
              </div>
            </div>
          </div>

          {/* Health score */}
          <div className="card-soft p-5 flex items-center gap-5">
            <div className="relative h-16 w-16 flex-shrink-0">
              <svg viewBox="0 0 36 36" className="h-16 w-16 -rotate-90">
                <circle
                  cx="18"
                  cy="18"
                  r="15.9"
                  fill="none"
                  stroke="rgba(16,185,129,0.12)"
                  strokeWidth="3"
                />
                <circle
                  cx="18"
                  cy="18"
                  r="15.9"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="3"
                  strokeDasharray={`${82 * 0.01 * 100} 100`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold text-emerald-600">82</span>
              </div>
            </div>
            <div>
              <div className="font-semibold text-slate-900 dark:text-white text-sm">
                Wealth Health Score
              </div>
              <div className="text-xs text-slate-500 mt-1 leading-relaxed">
                Good diversification. Consider increasing your emergency fund to
                push past 85.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Asset Coverage ────────────────────────────────────────────────────────────

function AssetCoverage() {
  return (
    <section className="border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">
            Asset Tracking
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white max-w-2xl mx-auto">
            If you own it,
            <span className="gradient-text"> you can track it.</span>
          </h2>
          <p className="mt-4 text-slate-600 dark:text-slate-300 max-w-lg mx-auto">
            17 asset categories covering everything from mainstream investments
            to niche holdings. No asset left behind, no number unaccounted for.
          </p>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6 gap-3 mb-10">
          {ASSET_TYPES.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="card-soft p-3 flex flex-col items-center gap-2 text-center group hover:border-emerald-200 dark:hover:border-emerald-500/30 transition-colors"
            >
              <div className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 group-hover:bg-emerald-100 transition-colors">
                <Icon className="h-4 w-4" strokeWidth={2} />
              </div>
              <span className="text-[11px] font-medium text-slate-700 dark:text-slate-300 leading-tight">
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Net worth equation */}
        <div className="card-soft p-6 flex flex-wrap items-center justify-center gap-4 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">
              All Your Assets
            </div>
            <div className="num-mono text-2xl font-bold text-slate-900 dark:text-white">
              {fmtINR(13200000)}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              across 17 categories
            </div>
          </div>
          <div className="text-2xl font-light text-slate-400">−</div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">
              All Your Liabilities
            </div>
            <div className="num-mono text-2xl font-bold text-red-500">
              {fmtINR(4174000)}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              loans, EMIs, cards
            </div>
          </div>
          <div className="text-2xl font-light text-slate-400">=</div>
          <div className="rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 px-6 py-3">
            <div className="text-[10px] uppercase tracking-widest text-emerald-600 mb-1">
              True Net Worth
            </div>
            <div className="num-mono text-2xl font-bold text-emerald-600">
              {fmtINR(9026000)}
            </div>
            <div className="text-xs text-emerald-600/70 mt-0.5">
              always up to date
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Liability Section ─────────────────────────────────────────────────────────

// ─── Projections ───────────────────────────────────────────────────────────────

function ProjectionsSection() {
  const scenarios = [
    {
      label: "Conservative",
      rate: "6% p.a.",
      value: fmtINR(34200000),
      color: "#f59e0b",
      dash: "4 4",
    },
    {
      label: "Moderate",
      rate: "10% p.a.",
      value: fmtINR(63800000),
      color: "#10b981",
      dash: "0",
    },
    {
      label: "Aggressive",
      rate: "14% p.a.",
      value: fmtINR(118500000),
      color: "#3b82f6",
      dash: "2 2",
    },
  ];

  return (
    <section className="border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">
              Wealth Projections
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white">
              See your wealth
              <br />
              <span className="gradient-text">at 50, 60, and 70.</span>
            </h2>
            <p className="mt-5 text-slate-600 dark:text-slate-300 text-base leading-relaxed">
              Stop guessing about retirement. Enter your current portfolio and
              contribution rate — AccessWealth shows you three honest
              projections over your lifetime, inflation-adjusted, so you know
              whether you're on track or need to change course.
            </p>
            <div className="mt-8 space-y-3">
              {scenarios.map((s) => (
                <div
                  key={s.label}
                  className="flex items-center gap-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-5 py-3.5"
                >
                  <div
                    className="h-3 w-3 rounded-full flex-shrink-0"
                    style={{ background: s.color }}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">
                      {s.label}
                    </div>
                    <div className="text-xs text-slate-500">
                      {s.rate} annual return
                    </div>
                  </div>
                  <div className="num-mono font-bold text-sm text-slate-900 dark:text-white">
                    {s.value}
                  </div>
                  <div className="text-xs text-slate-400">in 25 yrs</div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3 text-xs text-slate-500">
              {[
                "Retirement planning",
                "FIRE calculator",
                "Goal timelines",
                "Inflation-adjusted",
              ].map((t) => (
                <span
                  key={t}
                  className="flex items-center gap-1.5 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-3 py-1"
                >
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Chart */}
          <div className="card-soft p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                25-Year Net Worth Projection
              </div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">
                Starting: {fmtINR(9026000)}
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer>
                <ReAreaChart data={HERO_GROWTH}>
                  <defs>
                    <linearGradient id="proj-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="rgba(148,163,184,0.15)"
                  />
                  <XAxis
                    dataKey="year"
                    tick={{ fontSize: 10 }}
                    stroke="rgba(100,116,139,0.4)"
                  />
                  <YAxis
                    tickFormatter={(v) => fmtINR(v, { compact: true })}
                    tick={{ fontSize: 10 }}
                    stroke="rgba(100,116,139,0.4)"
                  />
                  <Tooltip
                    formatter={(v) => fmtINR(v)}
                    labelStyle={{ fontSize: 11 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fill="url(#proj-grad)"
                    name="Moderate (10%)"
                  />
                  <Area
                    type="monotone"
                    dataKey="invested"
                    stroke="#f59e0b"
                    strokeWidth={1.5}
                    fill="none"
                    strokeDasharray="4 4"
                    name="Conservative (6%)"
                  />
                </ReAreaChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-xs text-slate-500 text-center">
              Pre-loaded with sample data — sign up to model your actual
              portfolio
            </div>
          </div>
        </div>

        {/* Goals sub-section */}
        <div className="mt-16 pt-16 border-t border-slate-200 dark:border-slate-800">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-widest text-purple-600 mb-3">
              Goal Planning
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tighter text-slate-900 dark:text-white">
              Turn ambitions into tracked milestones.
            </h3>
            <p className="mt-3 text-slate-600 dark:text-slate-300 max-w-lg mx-auto text-sm">
              Create a goal, assign a target amount, link your assets to it, and
              let AccessWealth tell you when you'll get there — and what you
              need to contribute monthly to stay on track.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                emoji: "🏠",
                label: "Home purchase",
                target: "₹80L",
                progress: 64,
                due: "3.2 years",
              },
              {
                emoji: "🎓",
                label: "Child's education",
                target: "₹25L",
                progress: 38,
                due: "8 years",
              },
              {
                emoji: "🏖️",
                label: "Early retirement",
                target: "₹3Cr",
                progress: 22,
                due: "18 years",
              },
              {
                emoji: "✈️",
                label: "World trip",
                target: "₹5L",
                progress: 81,
                due: "5 months",
              },
            ].map((g) => (
              <div key={g.label} className="card-soft p-5">
                <div className="text-2xl mb-3">{g.emoji}</div>
                <div className="font-semibold text-slate-900 dark:text-white text-sm">
                  {g.label}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Target: {g.target}
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-500"
                    style={{ width: `${g.progress}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs font-semibold text-emerald-600">
                    {g.progress}% done
                  </span>
                  <span className="text-xs text-slate-400">{g.due} left</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Calculators ───────────────────────────────────────────────────────────────

function CalculatorsPreview() {
  const featured = CALCULATOR_LIST.slice(0, 8);
  return (
    <section
      id="calculators"
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200 dark:border-slate-800"
    >
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div className="max-w-xl">
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">
            25+ Calculators
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
            Every financial decision,
            <br />
            decoded.
          </h2>
          <p className="mt-3 text-slate-600 dark:text-slate-300">
            SIP returns, EMI affordability, retirement corpus, FIRE number, debt
            payoff timeline, tax savings — all with interactive charts. No
            Excel, no CA needed for the basics.
          </p>
        </div>
        <Link
          to="/calculators"
          data-testid="see-all-calcs"
          className="btn-secondary flex-shrink-0"
        >
          See all 25+ →
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {featured.map((c) => (
          <Link
            to={`/calculators/${c.slug}`}
            key={c.slug}
            data-testid={`calc-card-${c.slug}`}
            className="card-soft p-5 group"
          >
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
              <Calculator className="h-4 w-4" />
            </div>
            <h3 className="mt-4 font-display font-bold text-slate-900 dark:text-white">
              {c.name}
            </h3>
            <p className="mt-1 text-xs text-slate-500 line-clamp-2">
              {c.tagline}
            </p>
            <div className="mt-4 text-xs font-semibold text-emerald-600 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              Try it <ArrowRight className="h-3 w-3" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ─── Vault Section ─────────────────────────────────────────────────────────────

function VaultSection() {
  return (
    <section
      id="vault"
      className="relative overflow-hidden bg-slate-900 dark:bg-slate-950 text-white"
    >
      <div
        className="absolute inset-0 -z-0 opacity-30"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1648221350871-e3ae3c8d0f58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBsZWdhY3klMjBnZW5lcmF0aW9uJTIwc3Vuc2V0fGVufDB8fHx8MTc3ODc0NTEwN3ww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/40" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 text-xs font-semibold text-emerald-300 mb-5">
              <Heart className="h-3 w-3" /> Family Continuity Vault
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              You built it.
              <br />
              <span className="text-emerald-400">
                Make sure they can find it.
              </span>
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              AccessWealth isn't just for you — it's for the people who depend
              on you. The Family Continuity Vault lets you store your complete
              financial picture securely, assign nominees to every asset, and
              ensure your family has access when they need it most.
            </p>
            <div className="mt-8 p-5 rounded-xl bg-white/5 border border-white/10">
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-4">
                What you can store in the vault
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  "Will & testament",
                  "Life insurance policies",
                  "Property documents",
                  "FD records",
                  "Crypto recovery phrases",
                  "Bank account details",
                  "Locker details",
                  "Investment folios",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-2 text-sm text-slate-300"
                  >
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <Link
              to="/register"
              data-testid="vault-cta"
              className="mt-8 inline-flex items-center gap-2 btn-primary"
            >
              Start your continuity plan <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {VAULT_FEATURES.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="rounded-xl bg-white/5 border border-white/10 p-5 hover:bg-white/8 transition-colors"
              >
                <Icon className="h-5 w-5 text-emerald-400 mb-3" />
                <div className="font-semibold text-sm text-white mb-1">
                  {title}
                </div>
                <div className="text-xs text-slate-400 leading-relaxed">
                  {desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials ──────────────────────────────────────────────────────────────

function Testimonials() {
  const quotes = [
    {
      quote:
        "I had no idea what my actual net worth was until AccessWealth. Seeing everything in one number — including my loans — changed how I invest.",
      name: "Rahul M.",
      role: "Software Engineer, Pune",
    },
    {
      quote:
        "The EMI tracker alone is worth it. I used to dread month-end. Now I can see exactly what's due and when. No surprises.",
      name: "Priya S.",
      role: "Marketing Manager, Bengaluru",
    },
    {
      quote:
        "The 25-year projection made me increase my SIPs immediately. Seeing your future wealth in actual rupees is motivating in a way no article ever was.",
      name: "Vikram T.",
      role: "Finance Manager, Mumbai",
    },
  ];
  return (
    <section className="border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
            Why people use it.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {quotes.map((q) => (
            <div key={q.name} className="card-soft p-6">
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="h-3.5 w-3.5 fill-emerald-500 text-emerald-500"
                  />
                ))}
              </div>
              <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-5 italic">
                "{q.quote}"
              </p>
              <div>
                <div className="font-semibold text-sm text-slate-900 dark:text-white">
                  {q.name}
                </div>
                <div className="text-xs text-slate-500">{q.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ───────────────────────────────────────────────────────────────────────

function FAQ() {
  return (
    <section id="faq" className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20">
      <div className="text-center mb-10">
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
          Frequently asked
        </h2>
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          Everything you need to know before you start.
        </p>
      </div>
      <div className="space-y-3">
        {FAQ_ITEMS.map((it, i) => (
          <details
            key={i}
            className="card-soft p-5 group"
            data-testid={`faq-item-${i}`}
          >
            <summary className="cursor-pointer flex items-center justify-between gap-4 text-sm font-semibold text-slate-900 dark:text-white">
              {it.q}
              <span className="text-slate-400 group-open:rotate-45 transition-transform text-xl leading-none flex-shrink-0">
                +
              </span>
            </summary>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              {it.a}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}

// ─── CTA ───────────────────────────────────────────────────────────────────────

function CTA() {
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <div className="card-soft p-10 lg:p-16 relative overflow-hidden">
        <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="absolute -left-20 -top-20 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative text-center max-w-2xl mx-auto">
          <BadgeIndianRupee className="h-10 w-10 text-emerald-600 mx-auto" />
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
            Stop worrying about money.
            <br />
            Start understanding it.
          </h2>
          <p className="mt-3 text-slate-600 dark:text-slate-300">
            Start in 60 seconds. Pre-loaded with sample data so you can explore
            immediately. Free forever for core features.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/register"
              data-testid="bottom-cta-register"
              className="btn-primary"
            >
              Create my account — it's free
            </Link>
            <Link
              to="/login"
              data-testid="bottom-cta-login"
              className="btn-secondary"
            >
              Sign in
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
            {[
              "No credit card required",
              "Sample data pre-loaded",
              "AES-256 encrypted",
              "Cancel anytime",
            ].map((t) => (
              <span key={t} className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Contact ───────────────────────────────────────────────────────────────────

function ContactSection() {
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { name, email, message });
      setName("");
      setEmail("");
      setMessage("");
      toast.success("Message sent successfully!");
    } catch {
      toast.error("Failed to send message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="border-t border-slate-200 dark:border-slate-800 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20"
    >
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">
            Contact
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
            Questions, partnerships, or support?
          </h2>
          <p className="mt-4 text-slate-600 dark:text-slate-300 max-w-lg">
            Reach out to the AccessWealth team — we respond within 24–48 hours.
          </p>
          <div className="mt-8 space-y-5">
            {[
              { label: "General Support", value: "support@accesswealth.in" },
              { label: "Security Issues", value: "security@accesswealth.in" },
              { label: "Response Time", value: "Usually within 24–48 hours" },
            ].map((item) => (
              <div key={item.label}>
                <div className="text-xs uppercase tracking-widest text-slate-500">
                  {item.label}
                </div>
                <div className="mt-1 font-semibold text-slate-900 dark:text-white">
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="card-soft p-6">
          <form onSubmit={submit} className="space-y-4">
            {[
              {
                label: "Name",
                value: name,
                setter: setName,
                type: "text",
                placeholder: "Your name",
              },
              {
                label: "Email",
                value: email,
                setter: setEmail,
                type: "email",
                placeholder: "you@example.com",
              },
            ].map((f) => (
              <div key={f.label}>
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  {f.label}
                </label>
                <input
                  type={f.type}
                  required
                  value={f.value}
                  onChange={(e) => f.setter(e.target.value)}
                  className="input-soft mt-1"
                  placeholder={f.placeholder}
                />
              </div>
            ))}
            <div>
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Message
              </label>
              <textarea
                required
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="input-soft mt-1 resize-none"
                placeholder="How can we help?"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
